import React, { CSSProperties, useRef } from 'react';
import cn from 'classnames';
import { IProduct } from 'base/interfaces/product';
import { getTargetImage } from 'base/utils/getPicUrl';
import ListProductQuickView, { IQuickViewData } from './ListProductQuickView';
import ListProductSoldOut from './ListProductSoldOut';
import { get750RemValue } from 'base/utils/dom-size';
import Image from '@base/lib/ThemeImage';
import type { ThemeImageProps } from '@base/lib/ThemeImage/interface';
import { IElementTarget } from 'base/interfaces/common';
import ListProductTags from '@base/snippets/ListProduct/ListProductTags';
import ListProductImageHover from '@base/snippets/ListProduct/ListProductImageHover';
import { TDiscountStyle } from 'mod/product/interface'

export type IShowTag = boolean | {       // 对象对每一个单独控制展示
    showProductTag?: boolean;    // 商品tag
    showActivityTag?: boolean;      // 活动标签展示
    showActivityOffTag?: boolean;   // 活动标签 折扣 展示
    showSuperFlashSaleTag?: boolean;   // 活动标签 折扣 展示
};

function getSpaceValue(space: number | string): string {
    const isPhone = process.env.NEXT_PUBLIC_TERMINAL === 'm';
    return typeof space === 'number'
        ? (isPhone ? get750RemValue(space) : `${space}px`)
        : space;
}

export type ListProductImageProps = Omit<ThemeImageProps, 'src' | 'width' | 'height'> & {
    moduleElementId?: string;
    isHoverChangeAsset?: boolean;
    product: IProduct;
    imageSize?: number;
    spaceAround?: number;
    spaceBelow?: number;
    canJump?: boolean;
    canShowSoldOut?: boolean;
    aTarget?: IElementTarget;  // <a>标签跳转方式
    showTag?: IShowTag; //是否展示标签，默认不展示
    offTagShow?: TDiscountStyle; // 折扣标签 展示 '-' 还是 'OFF'
    /** productTag
     * 自定义商品Tag (根据 productTags 展示商品tag)， 强等false不展示
     * 中配置为对象是里面的productTag也用
     * */
    productTag?: React.ReactNode | false
    activityTag?: React.ReactNode | false; // 活动标签和折扣Deal标签
    imgExtra?: React.ReactNode | false; // 额外展示
    onQuickView?: (variantId: string) => void;
    onClick?: () => void;
    'data-t-tar'?: string;
    'data-t-pos'?: string;
    'data-t-vid'?: string;
    'data-t-price'?: string;
};

function getWrapStyle(spaceAround?: number, spaceBelow?: number): CSSProperties | undefined {
    let wrapStyle: CSSProperties | undefined;
    if (spaceAround || spaceBelow) {
        wrapStyle = {} as CSSProperties;
        if (spaceAround) {
            wrapStyle.padding = getSpaceValue(spaceAround);
        }
        if (spaceBelow) {
            wrapStyle.marginBottom = getSpaceValue(spaceBelow);
        }
    }
    return wrapStyle;
}

const ListProductImage = React.forwardRef<HTMLDivElement, ListProductImageProps>((props, ref) => {
    const hoverImageRef = useRef<HTMLDivElement>(null);

    const {
        className,
        moduleElementId = '',
        isHoverChangeAsset = false,
        aTarget = '_self',
        product,
        imageSize = 365,
        spaceAround,
        spaceBelow,
        canJump = true,
        canShowSoldOut = true,
        showTag = false,
        offTagShow,
        productTag,
        imgExtra,
        onQuickView,
        onClick,
        'data-t-tar': dataTrackTarget,
        'data-t-pos': dataTrackPosition,
        'data-t-vid': dataTrackVariantId,
        'data-t-price': dataTrackPrice,
        ...rest
    } = props;

    const { link, images, variantId, name, isStock } = product;
    let { picLink, altText, hoverAssetLink, hoverAltText } = product;

    // 兼容未过滤字段的列表
    if (typeof images !== 'undefined') {
        const mainImage = getTargetImage(images, variantId, 'isMain');
        const hoverImage = getTargetImage(images, variantId, 'isListHover');
        picLink = mainImage?.picLink || '';
        altText = mainImage?.altText || '';
        hoverAssetLink = hoverImage?.picLink || '';
        hoverAltText = hoverImage?.altText || '';
    }

    const itemImageClass = `js-product-img-${variantId}-${moduleElementId}`

    return (
        <div className={cn(className, itemImageClass, 'g-picture')} ref={hoverImageRef}>
            <div
                className="block picture relative js-picture"
                ref={ref}
                data-t-tar={dataTrackTarget}
                data-t-pos={dataTrackPosition}
                data-t-vid={dataTrackVariantId}
                data-t-price={dataTrackPrice}
                onClick={onClick}
                style={getWrapStyle(spaceAround, spaceBelow)}
            >
                {
                    (isHoverChangeAsset && !!hoverAssetLink) ?
                        <ListProductImageHover
                            itemImageClass={itemImageClass}
                            hoverRef={hoverImageRef}
                            aTarget={aTarget}
                            product={product}
                            linkProps={{
                                hoverAssetLink: hoverAssetLink,
                                hoverAltText: hoverAltText,
                                picLink: picLink,
                                altText: altText
                            } as any}
                            imageSize={imageSize}
                            {...rest}
                        />
                        : <Image
                            {...rest}
                            target={aTarget}
                            href={canJump ? link : void 0}
                            src={picLink}
                            alt={altText || name}
                            width={imageSize}
                            height={imageSize}
                            className={canJump ? 'js-track-link' : void 0}
                        />
                }

                {/* 商品tags */}
                <ListProductTags
                    showTag={showTag}
                    product={product}
                    offTagShow={offTagShow}
                />

                {imgExtra}

                {(!isStock && canShowSoldOut) && <ListProductSoldOut product={product} canJump={canJump}/>}

                {typeof onQuickView === 'function' && (
                    <ListProductQuickView
                        product={product}
                        onQuickView={onQuickView}
                    />
                )}

            </div>

        </div>
    )
});

export default ListProductImage;
